import { useState, useEffect, useContext } from "react";
import InputField from "@/components/molecules/InputField";
import Button from "@/atoms/Button";
import Para from "@/atoms/text/Para";
import { SuccessIcon } from "@/atoms/Icons";
import H3 from "@/atoms/text/H3";
import H2 from "@/atoms/text/H2";
import { BRAND_ID, IS_LIVE } from "@/utils/constants";
import { getRefinedLeadInfo, processLeadSuccess } from "@/utils/registerUtil";
import { makeLeadRegistrationRequest } from "@/utils/fetchHelper";
import {
  highlightInvalidFields,
  getCookieValue,
  onFocus,
  isLeadInfoValid,
} from "@/utils/commonUtil";
import { GlobalContext } from "@/components/Theme";
import { isMobileScreen } from "@/utils/functions";
import { useRouter } from "next/router";
function RegistrationForm({
  title,
  btnName = "See Prices",
  hasFormCardTitle = false,
  bgColor = "white",
  content,
  className,
  hasBusinessNameField = true,
  variant = "default",
  formUniqueName,
  formOnHero = false,
}) {
  const router = useRouter();
  const [leadInfo, setLeadInfo] = useState({});
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [globalState, setGlobalState] = useContext(GlobalContext);
  const [isMobile, setIsMobile] = useState(null);

  const AddAriaLabelDialCode = () => {
    useEffect(() => {
      const selectedFlagElement = document.querySelector(".iti__selected-flag");
      if (selectedFlagElement) {
        selectedFlagElement.setAttribute("aria-label", "Select your country");
      }
    }, []);
    return null;
  };

  useEffect(() => {
    setIsMobile(isMobileScreen);
  }, []);

  useEffect(() => {
    // This effect will run once `isButtonLoading` is set to `false` after successful registration and route change
    if (!isButtonLoading && globalState.isRegistered) {
      setGlobalState((prevState) => ({
        ...prevState,
        isRegistered: true, // Update global state after form submission & route change
      }));
    }
  }, [isButtonLoading, globalState.isRegistered, setGlobalState]);

  function onBlur(event, info) {
    const blurredElement = event.target;
    if (info.valid) {
      blurredElement.classList.remove("error");
    } else {
      blurredElement.classList.add("error");
    }
    setLeadInfo((prevLeadInfo) => ({
      ...prevLeadInfo,
      [info.emittedBy]: info,
    }));
  }

  const successCallback = (response) => {
    if (router.asPath.startsWith("/lp/ria-affinity-program")) {
      router.push("/plans-ria-affinity");
    } else {
      router.push("/plans");
    }
    processLeadSuccess(response);
    setIsButtonLoading(false);
  };

  const failureCallback = (response) => {
    setIsButtonLoading(false);
  };

  const formSubmitHandler = (e) => {
    e.preventDefault();
    if (isLeadInfoValid(leadInfo)) {
      setIsButtonLoading(true);

      const finalPaylod = {
        brandid: BRAND_ID,
        cookie: document.cookie,
        referrer: window.location.href,
        timezone: window.getUserTimezone && window.getUserTimezone(),
        ...(router.asPath.startsWith("/lp/ria-affinity-program") && {
          tagName: "ria affinity program",
        }),
        ...getRefinedLeadInfo(leadInfo),
      };

      makeLeadRegistrationRequest({
        payload: finalPaylod,
        successCallback,
        failureCallback,
      });
    } else {
      highlightInvalidFields(leadInfo);
    }
  };

  function initialiseRegistrationFold() {
    console.log("registration intialisation starting...");
    const validCountryCodes = ["US", "CA", "GB", "IN"];
    const countryCode = validCountryCodes.includes(
      getCookieValue("countryCode")
    )
      ? getCookieValue("countryCode")
      : "US";

    const leadInfoTemp =
      window.InputValidator &&
      window.InputValidator.fields.initialize({
        fields: {
          phone: {
            selector: `#${formUniqueName}-phone`,
            type: "phone",
            withDialCodePicker: true,
            country: countryCode,
            errorClassName: "error",
          },
          mail: {
            selector: `#${formUniqueName}-email-field`,
            type: "email",
            errorClassName: "error",
          },
          name: {
            selector: `#${formUniqueName}-name-field`,
            type: "text",
            errorClassName: "error",
          },
          ...(hasBusinessNameField
            ? {
                company: {
                  selector: `#business-name-field`,
                  type: "text",
                  errorClassName: "error",
                },
              }
            : {}),
        },
        eventListeners: {
          blur: onBlur,
          focus: onFocus,
        },
      });
    setLeadInfo(leadInfoTemp);
  }

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.InputValidator &&
      window.InputValidator.fields
    ) {
      initialiseRegistrationFold();
    }
  }, []);

  return !globalState.isRegistered ? (
    <>
      {variant === "default" ? (
        <div
          className={`form ${
            formOnHero ? "formOnHero" : "formOnLeft"
          } ${className}`}
        >
          {title && (
            <H2
              desktopSize={`${
                hasFormCardTitle ? "xxs" : formOnHero ? "sm" : "lg"
              }`}
              mobileSize={`${
                hasFormCardTitle ? "xxs" : formOnHero ? "sm" : "sm"
              }`}
              className={`${
                hasFormCardTitle
                  ? "mb-5-d mb-7"
                  : formOnHero
                  ? "bold mb-2-d mb-7"
                  : "bold mb-7-d mb-5"
              }`}
            >
              {title}
              <span className="dot">.</span>
            </H2>
          )}
          <InputField
            label="Name"
            id={`${formUniqueName}-name-field`}
            name="name"
            aria-label="Your name"
            className="mb-6"
            placeholder="Your name"
          />
          <InputField
            label="Email"
            id={`${formUniqueName}-email-field`}
            name="email"
            aria-label="Your email address"
            className="mb-6"
            placeholder="Your email address"
          />
          {hasBusinessNameField && (
            <InputField
              label="Business name"
              id="business-name-field"
              name="email"
              aria-label="Your business name"
              className="mb-6"
              placeholder="Your business name"
            />
          )}
          <AddAriaLabelDialCode />
          <InputField
            id={`${formUniqueName}-phone`}
            name="phone"
            aria-label="Your phone number"
            className="phone mb-6"
            type="tel"
            placeholder="Phone number"
          />
          <Button
            type="submit"
            href="#"
            size="md"
            className={`w-100 tc-black ${isButtonLoading ? "loading" : ""}`}
            onClick={formSubmitHandler}
            aria-label="Submit the form"
          >
            {btnName}
          </Button>
          <Para
            color="grey"
            desktopSize="xs"
            mobileSize="xs"
            className="ta-left mt-3"
          >
            We'll always keep your details secure & private. By submitting this
            form you <br className="desktop-only" />
            agree to our{" "}
            <a className="text--link" href="/privacypolicy">
              privacy policy
            </a>
            .
          </Para>
          <style jsx>{`
            .form {
              padding: 40px;
              border-radius: 16px;
              max-width: 500px;
            }
            .form.formOnLeft {
              padding: 0;
            }
            .form.formOnHero {
              background-color: #ffffff;
            }
            @media (max-width: 500px) {
              .form {
                border-radius: 0;
                padding: 60px 20px;
                min-width: auto;
              }
            }
            @media (max-width: 1199px) {
              .form {
                margin: 0 auto;
                border-radius: initial;
                max-width: initial;
              }
              .form.formOnHero {
                background-color: #edf4fc;
              }
            }
            .form.horizontal-form {
              max-width: 100%;
              padding: 0;
            }
          `}</style>
        </div>
      ) : (
        variant === "mini" && (
          <section className="pv-15-d pv-8 bc-grad-blue">
            <div className="container">
              <div className={`form ${className} horizontal-form`}>
                {title && (
                  <H2
                    className="bold mb-5 ta-center-d"
                    color="white"
                    dangerouslySetInnerHTML={{
                      __html: `${title}<span class="dot">.</span>`,
                    }}
                  />
                )}
                {content && (
                  <Para
                    desktopSize="lg"
                    mobileSize="lg"
                    color="white"
                    className="mb-8-d mb-7 ta-center-d"
                  >
                    {content}
                  </Para>
                )}
                <div className="fx-d fx--sb">
                  <InputField
                    label="Name"
                    id={`${formUniqueName}-name-field`}
                    name="name"
                    aria-label="Your name"
                    className="mr-5-d mb-6 mb-reset-d"
                    placeholder="Your name"
                  />
                  <InputField
                    label="Email"
                    id={`${formUniqueName}-email-field`}
                    name="email"
                    aria-label="Your email address"
                    className="mr-5-d mb-6 mb-reset-d"
                    placeholder="Your email address"
                  />
                  {hasBusinessNameField && (
                    <InputField
                      label="Business name"
                      id="business-name-field"
                      name="email"
                      aria-label="Your business name"
                      className="mr-5-d mb-6 mb-reset-d"
                      placeholder="Your business name"
                    />
                  )}
                  <AddAriaLabelDialCode />
                  <InputField
                    id={`${formUniqueName}-phone`}
                    name="phone"
                    aria-label="Your phone number"
                    className="phone mr-5-d mb-6 mb-reset-d"
                    type="tel"
                    placeholder="Phone number"
                  />
                  <Button
                    type="submit"
                    href="#"
                    size="md"
                    className={`w-100 tc-black ${
                      isButtonLoading ? "loading" : ""
                    }`}
                    onClick={formSubmitHandler}
                    aria-label="Submit the form"
                  >
                    {btnName}
                  </Button>
                </div>
                <style jsx>{`
                  .form.horizontal-form {
                    max-width: 100%;
                    padding: 0;
                  }
                `}</style>
              </div>
            </div>
          </section>
        )
      )}
    </>
  ) : variant === "default" ? (
    <div
      className={`form bc-${
        !isMobile && formOnHero ? "white" : bgColor
      } ${className} fx fx--c fx--ch fx--cv ta-center`}
    >
      <SuccessIcon className="mb-3" />
      <H3 desktopSize="lg" className="bold mb-5">
        Thank you for registering!
      </H3>
      <Para desktopSize="lg" mobileSize="lg" className="mb-7">
        Find the right plan for your business.
      </Para>
      <Button
        href={
          IS_LIVE
            ? "https://www.answerforce.com/plans"
            : "https://testing.answerforce.com/plans"
        }
        size="md"
        variant="primary"
        aria-label="Submit the form"
      >
        See Prices
      </Button>
      <style jsx>{`
        .form {
          padding: 40px;
          border-radius: 16px;
          max-width: 500px;
          max-height: 526px;
          height: 100%;
        }
        @media (max-width: 500px) {
          .form {
            border-radius: 0;
            min-width: auto;
            padding: 120px 20px;
          }
        }
        @media (max-width: 1199px) {
          .form {
            margin: 0 auto;
            border-radius: initial;
            max-width: initial;
          }
          .form.formOnHero {
            background-color: #edf4fc;
          }
        }
        .form.formOnLeft {
          padding: 0;
        }
      `}</style>
    </div>
  ) : (
    variant === "mini" && (
      <section className="pv-15-d pv-8 bc-grad-blue">
        <div className="container">
          <div
            className={`form horizontal-form ${className} fx fx--c fx--ch fx--cv ta-center`}
          >
            <SuccessIcon
              className="mb-3"
              circleColor="white"
              tickColor="white"
            />
            <H3 desktopSize="lg" className="bold mb-5" color="white">
              Thank you for registering<span className="dot">.</span>
            </H3>
            <Para
              desktopSize="lg"
              mobileSize="lg"
              className="mb-7"
              color="white"
            >
              Find the right plan for your business.
            </Para>
            <Button
              href={
                IS_LIVE
                  ? "https://www.answerforce.com/plans"
                  : "https://testing.answerforce.com/plans"
              }
              size="md"
              variant="primary"
              aria-label="Submit the form"
            >
              See Prices
            </Button>
            <style jsx>{`
              .form.horizontal-form {
                max-width: 100%;
                padding: 0;
              }
            `}</style>
          </div>
        </div>
      </section>
    )
  );
}

export default RegistrationForm;
